import React, { useState } from "react";
import { createPortal } from "react-dom";
import CustomSelect from "./CustomSelect";
import RoleShareToggle from "./RoleShareToggle";
import UserSearchInput from "./UserSearchInput";
import { IconClose } from "./Icons";
import "./ShareEnvironmentModal.css"; // share-chips / share-everyone styles

/** Create/edit form for a user-defined agent. `agent` = null for create;
 *  otherwise the full owner projection from api.getAgent. */
function CheckList({ items, selected, onToggle, empty }) {
  if (!items.length) return <div className="ca-agent-modal-empty">{empty}</div>;
  return (
    <div className="ca-agent-modal-checklist">
      {items.map((it) => (
        <label key={it.key} className="ca-agent-modal-check">
          <input type="checkbox" checked={selected.includes(it.key)}
                 onChange={() => onToggle(it.key)} />
          <span>{it.label}</span>
          {it.hint && <span className="ca-agent-modal-check-hint">{it.hint}</span>}
        </label>
      ))}
    </div>
  );
}

const toggleIn = (list, key) =>
  list.includes(key) ? list.filter((k) => k !== key) : [...list, key];

export default function AgentModal({ agent, models, skills = [], mcpServers = [], onClose, onSave, onDelete }) {
  const [name, setName] = useState(agent?.name || "");
  // Legacy agents (no pinned set) start from what they'd inherit today: the
  // user's active skills and enabled MCP servers. Saving always pins a list.
  const [skillIds, setSkillIds] = useState(
    agent?.skill_ids ?? skills.filter((s) => s.is_active).map((s) => s._id)
  );
  const [mcpUrls, setMcpUrls] = useState(
    agent?.mcp_server_urls ?? mcpServers.filter((s) => s.connected && s.enabled).map((s) => s.url)
  );
  const [description, setDescription] = useState(agent?.description || "");
  // "" = follow the admin's default model (whatever is top-ranked when a chat starts).
  const [model, setModel] = useState(agent?.model || "");
  const [systemPrompt, setSystemPrompt] = useState(agent?.system_prompt || "");
  // Sharing mirrors environments: users + roles + everyone, OR'd. All empty =
  // just you.
  const [sharedUsers, setSharedUsers] = useState(agent?.shared_with_users || []);
  const [sharedRoles, setSharedRoles] = useState(agent?.shared_with_roles || []);
  const [sharedEveryone, setSharedEveryone] = useState(!!agent?.shared_with_everyone);
  const addSharedUser = (u) => {
    const email = (u.email || "").trim().toLowerCase();
    if (email) setSharedUsers((v) => (v.includes(email) ? v : [...v, email].sort()));
  };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const canSave = name.trim() && systemPrompt.trim();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        model: model || null,
        system_prompt: systemPrompt.trim(),
        skill_ids: skillIds,
        mcp_server_urls: mcpUrls,
        shared_with_users: sharedUsers,
        shared_with_roles: sharedRoles,
        shared_with_everyone: sharedEveryone,
      });
    } catch (e2) {
      setErr(e2.message || "Failed to save agent");
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${agent.name}"? Existing chats keep working but the agent disappears from the picker.`)) return;
    setBusy(true); setErr(null);
    try {
      await onDelete();
    } catch (e2) {
      setErr(e2.message || "Failed to delete agent");
      setBusy(false);
    }
  };

  return createPortal(
    <div className="ca-mcp-modal-backdrop" onClick={onClose}>
      <form className="ca-mcp-modal ca-agent-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>{agent ? "Edit agent" : "New agent"}</h3>
        <label>
          Name
          <input className="ca-input" value={name} autoFocus
                 onChange={(e) => setName(e.target.value)}
                 placeholder="TP Story Generator" />
        </label>
        <label>
          Description
          <input className="ca-input" value={description}
                 onChange={(e) => setDescription(e.target.value)}
                 placeholder="What this agent is for (shown in the picker)" />
        </label>
        <label>
          Model
          <CustomSelect
            value={model}
            options={[
              { value: "", label: `Default (${models.find((m) => m.is_default)?.label || "admin's choice"})` },
              ...models.map((m) => ({ value: m.id, label: m.label })),
            ]}
            onChange={setModel}
            ariaLabel="Model"
          />
        </label>
        <label>
          System prompt
          <textarea className="ca-input" rows={8} value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    placeholder="You are…" />
        </label>
        <label>
          Skills
          <CheckList
            items={skills.map((s) => ({ key: s._id, label: s.name }))}
            selected={skillIds}
            onToggle={(k) => setSkillIds((v) => toggleIn(v, k))}
            empty="No skills in your library yet."
          />
        </label>
        <label>
          MCP servers
          <CheckList
            items={mcpServers.map((s) => ({
              key: s.url, label: s.name, hint: s.connected ? undefined : "not connected",
            }))}
            selected={mcpUrls}
            onToggle={(k) => setMcpUrls((v) => toggleIn(v, k))}
            empty="No MCP servers available."
          />
        </label>
        <div className="ca-agent-modal-group">
          Sharing
          <div className="ca-agent-modal-sharing">
            {sharedUsers.length > 0 && (
              <div className="share-chips">
                {sharedUsers.map((u) => (
                  <span key={u} className="share-chip">
                    {u}
                    <button type="button" title="Remove" aria-label={`Remove ${u}`}
                            onClick={() => setSharedUsers((v) => v.filter((x) => x !== u))}>
                      <IconClose size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <UserSearchInput onSelectUser={addSharedUser} placeholder="Share with a person…" />
            <RoleShareToggle value={sharedRoles} onChange={setSharedRoles} />
            <span className="share-everyone">
              <input type="checkbox" checked={sharedEveryone}
                     onChange={(e) => setSharedEveryone(e.target.checked)} />
              <span><strong>Everyone in the org</strong></span>
            </span>
            {!sharedUsers.length && !sharedRoles.length && !sharedEveryone && (
              <span className="ca-agent-modal-empty">Only you can use this agent.</span>
            )}
          </div>
        </div>
        {err && <div className="ca-mcp-modal-err">{err}</div>}
        <div className="ca-mcp-modal-actions">
          {agent && onDelete && (
            <button type="button" className="ca-btn ca-btn--sm"
                    onClick={remove} disabled={busy}
                    style={{ marginRight: "auto", color: "var(--ca-danger)" }}>
              Delete
            </button>
          )}
          <button type="button" className="ca-btn ca-btn--sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="ca-btn ca-btn--sm ca-btn--primary" disabled={busy || !canSave}>
            {busy ? "Saving…" : agent ? "Save" : "Create agent"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
